import { Control, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/core/components/ui/form";
import { Input } from "src/core/components/ui/input";
import { cn } from "src/core/lib/utils";

type FormInputProps<T extends FieldValues> = {
  type?: "text" | "date";
  name: FieldPath<T>;
  label: string;
  placeholder?: string;
  required?: boolean;
  softRequired?: boolean;
  className?: string;
  control?: Control<T>;
  disabled?: boolean;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  borderErrorClass?: string;
  labelErrorClass?: string;
};

/**
 *
 * @param softRequired - (boolean) Permet de gérer un affichage de type "warning" (orange)
 * pour les champs qui sont nécessaires à la validation d'un dossier.
 */
export function FormInput<T extends FieldValues>({
  type = "text",
  name,
  label,
  placeholder,
  required = false,
  softRequired = false,
  className = "w-[300px]",
  control,
  disabled = false,
  onPaste,
  onChange,
  borderErrorClass = "border-alert",
  labelErrorClass = "text-alert",
}: FormInputProps<T>) {
  const { t } = useTranslation();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const isInvalid = fieldState.invalid;
        const showSoftRequired = softRequired && !isInvalid;

        return (
          <FormItem>
            <FormLabel required={required} className={cn(isInvalid && labelErrorClass)}>
              {label}
            </FormLabel>
            <FormControl>
              <Input
                className={cn(
                  className,
                  isInvalid && borderErrorClass,
                  showSoftRequired && "border-2 border-dashed border-orange-medium"
                )}
                placeholder={placeholder ? placeholder : t("global.form.input.placeholder")}
                disabled={disabled}
                onPaste={onPaste}
                {...field}
                value={field.value ?? ""}
                onChange={onChange || field.onChange}
                type={type}
                variant={type === "date" ? "inline" : "default"}
              />
            </FormControl>
            <FormMessage className={cn(className)} />
            {showSoftRequired && (
              <p className="font-light text-sm text-muted">
                {t("global.form.warning.softRequired")}
              </p>
            )}
          </FormItem>
        );
      }}
    />
  );
}
